<script setup>
import { Head } from '@inertiajs/vue3'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import InquireForm from '@/Components/Public/InquireForm.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  vehicle:   { type: Object, required: true },
  base_path: { type: String, default: '/rentals' }
})
const { t } = useI18n()

const photos = ref(props.vehicle.photo_urls?.length ? props.vehicle.photo_urls : [])
const activeIndex = ref(0)
</script>

<template>
  <Head>
    <title>{{ vehicle.make }} {{ vehicle.model }} — {{ t('public.catalog.head_title') }}</title>
  </Head>

  <section class="detail">
    <a :href="base_path" class="back">← {{ t('public.catalog.back') }}</a>

    <div class="layout">
      <div class="gallery">
        <div class="gallery__main">
          <img v-if="photos[activeIndex]" :src="photos[activeIndex]" :alt="`${vehicle.make} ${vehicle.model}`" />
          <CatalogPlaceholder v-else kind="vehicle" :category="vehicle.category" />
        </div>
        <ul v-if="photos.length > 1" class="gallery__thumbs">
          <li v-for="(p, i) in photos" :key="p">
            <button :class="['thumb', i === activeIndex && 'is-active']" @click="activeIndex = i">
              <img :src="p" :alt="''" />
            </button>
          </li>
        </ul>
      </div>

      <div class="info">
        <p class="info__class">{{ t(`public.classes.${vehicle.vehicle_class}`) }} · {{ t(`public.categories.${vehicle.category}`) }}</p>
        <h1 class="info__title">{{ vehicle.make }} {{ vehicle.model }} <span v-if="vehicle.year" class="info__year">({{ vehicle.year }})</span></h1>
        <p class="info__price"><span class="fw-value">{{ vehicle.daily_rate }}</span> <span class="info__per">{{ t('public.catalog.per_day') }}</span></p>
        <p v-if="vehicle.deposit" class="info__deposit">{{ t('public.catalog.deposit') }}: <span class="fw-value">{{ vehicle.deposit }}</span></p>

        <dl class="specs">
          <template v-if="vehicle.engine_cc"><dt>{{ t('public.catalog.specs.engine') }}</dt><dd>{{ vehicle.engine_cc }}cc</dd></template>
          <template v-if="vehicle.colour"><dt>{{ t('public.catalog.specs.colour') }}</dt><dd>{{ vehicle.colour }}</dd></template>
          <template v-if="vehicle.transmission"><dt>{{ t('public.catalog.specs.transmission') }}</dt><dd>{{ t(`public.transmissions.${vehicle.transmission}`) }}</dd></template>
          <template v-if="vehicle.fuel_type"><dt>{{ t('public.catalog.specs.fuel') }}</dt><dd>{{ t(`public.fuels.${vehicle.fuel_type}`) }}</dd></template>
          <template v-if="vehicle.seats"><dt>{{ t('public.catalog.specs.seats') }}</dt><dd>{{ vehicle.seats }}</dd></template>
        </dl>

        <div class="ctas">
          <!-- Only render Write-on-X CTAs for message-type channels the
               operator has actually configured (BrandChannels.chat_intents
               returns only telegram + whatsapp that exist). Two-platform
               cap on visual primary/secondary; anything beyond falls back
               to the secondary style. No-channel → no button, never a
               dead link. -->
          <a
            v-for="(url, platform, idx) in vehicle.contact_intents"
            :key="platform"
            :class="['cta', idx === 0 ? 'cta--primary' : 'cta--secondary']"
            :href="url"
            target="_blank"
            rel="noopener"
          >
            {{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}
          </a>
          <a class="cta cta--tertiary" href="#inquire">{{ t('public.catalog.ctas.form') }}</a>
        </div>
      </div>
    </div>

    <div id="inquire" class="inquire">
      <h2 class="inquire__title">{{ t('public.inquire.title') }}</h2>
      <p class="inquire__lede">{{ t('public.inquire.lede') }}</p>
      <InquireForm :topic="'rental'" :vehicle-id="vehicle.id" :prefill-message="`${t('public.inquire.prefill_vehicle')} ${vehicle.make} ${vehicle.model}`" />
    </div>
  </section>
</template>

<style scoped>
.detail { max-width: 72rem; margin: 0 auto; padding: 1.5rem 1.5rem 4rem; width: 100%; }
.back { display: inline-block; color: var(--muted); text-decoration: none; margin-bottom: 1rem; font-size: 0.875rem; }
.back:hover { color: var(--ink); }

.layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.2fr 1fr; gap: 2.5rem; } }

.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 0.75rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder { font-size: 4rem; font-weight: 700; color: var(--muted); font-family: var(--font-display, var(--display)); letter-spacing: 0.05em; }
.gallery__thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 4.5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }

.info__class  { font-size: 0.75rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 0.25rem; }
.info__title  { font-family: var(--font-display, var(--display)); font-size: clamp(1.75rem, 3.5vw, 2.5rem); letter-spacing: -0.02em; margin: 0 0 0.5rem; color: var(--ink); font-weight: 800; }
.info__year   { color: var(--muted); font-weight: 400; }
.info__price  { margin: 0 0 0.25rem; font-size: 1.25rem; color: var(--ink); }
.info__per    { font-size: 0.875rem; color: var(--muted); margin-left: 0.25rem; }
.info__deposit { margin: 0 0 1rem; font-size: 0.9375rem; color: var(--inkSoft); }

.specs { display: grid; grid-template-columns: 8rem 1fr; gap: 0.4rem 1rem; margin: 0 0 1.5rem; font-size: 0.9375rem; }
.specs dt { color: var(--muted); margin: 0; }
.specs dd { margin: 0; color: var(--ink); }

.ctas { display: grid; gap: 0.5rem; }
.cta { display: block; padding: 0.75rem 1rem; border-radius: 0.625rem; text-align: center; text-decoration: none; font-weight: 600; font-size: 0.9375rem; }
.cta--primary   { background: var(--accent);    color: var(--accentInk); }
.cta--secondary { background: var(--surfaceAlt); color: var(--ink); border: 1px solid var(--line); }
.cta--tertiary  { background: transparent; color: var(--ink); border: 1px solid var(--line); }

.inquire { margin-top: 3rem; padding-top: 2rem; border-top: 1px solid var(--line); }
.inquire__title { font-family: var(--font-display, var(--display)); font-size: 1.5rem; margin: 0 0 0.25rem; color: var(--ink); font-weight: 600; }
.inquire__lede  { color: var(--inkSoft); margin: 0 0 1.5rem; }
</style>
